import React from 'react'
import PropTypes from 'prop-types'
import Downshift from 'downshift'
import getOr from 'lodash/fp/getOr'
import find from 'lodash/fp/find'

import Wrapper from './Wrapper'
import AutocompleteTextField from './AutocompleteTextField'
import AutocompleteMenu from './AutocompleteMenu'
import { makeSearchPattern } from './utils'

const callAll = (...fns) => (...args) => fns.forEach(fn => fn && fn(...args))
const defaultGetItemValue = getOr('', 'value')
const defaultGetItemText = getOr('', 'text')

const makeHandleChange = ({ onChange, getItemValue }) =>
  selected => onChange && onChange(getItemValue(selected))

const getItemBySelector = itemValueSelector =>
  value => item => value === itemValueSelector(item)

function AutocompleteField({
  defaultValue,
  value,
  name,
  id = name,
  icon,
  items,
  onChange,
  getItemValue,
  getItemText,
  ...props
}) {
  const findItemByValue = getItemBySelector(getItemValue)
  const selectedItem = value
    ? find(findItemByValue(value))(items) || null
    : value
  const initialSelectedItem = defaultValue
    ? find(findItemByValue(defaultValue))(items)
    : value
  return (
    <Downshift
      id={id}
      itemToString={getItemText}
      onChange={makeHandleChange({ onChange, getItemValue })}
      selectedItem={selectedItem}
      initialSelectedItem={initialSelectedItem}
    >
      {({
        getRootProps,
        getInputProps,
        getMenuProps,
        getItemProps,
        getToggleButtonProps,
        openMenu,
        clearSelection,
        isOpen,
        selectedItem,
        inputValue,
        highlightedIndex
      }) => {
        const search = inputValue ? makeSearchPattern(inputValue) : null

        return (
          <Wrapper
            full={props.full}
            {...getRootProps({ refKey: 'ref' })}
          >
            <AutocompleteTextField
              {...getInputProps({
                ...props,
                name,
                onFocus: callAll(props.onFocus, openMenu)
              })}
              icon={icon}
              selectedItem={selectedItem}
              clearSelection={clearSelection}
              getToggleButtonProps={getToggleButtonProps}
            />
            <AutocompleteMenu
              isOpen={isOpen}
              items={items}
              search={search}
              highlightedIndex={highlightedIndex}
              getMenuProps={getMenuProps}
              getItemProps={getItemProps}
              getItemValue={getItemValue}
              getItemText={getItemText}
            />
          </Wrapper>
        )
      }}
    </Downshift>
  )
}

AutocompleteField.propTypes = {
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number
  ]),
  defaultValue: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number
  ]),
  name: PropTypes.string.isRequired,
  id: PropTypes.string,
  items: PropTypes.array.isRequired,
  icon: PropTypes.oneOfType([
    PropTypes.element,
    PropTypes.bool
  ]),
  fullWidth: PropTypes.bool,
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  getItemValue: PropTypes.func.isRequired,
  getItemText: PropTypes.func.isRequired
}

AutocompleteField.defaultProps = {
  getItemValue: defaultGetItemValue,
  getItemText: defaultGetItemText
}

export default AutocompleteField
